import { AlertTriangle } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import type { AvailabilityWarning } from '../domain/types';

export interface ScheduleAvailabilityWarningProps {
  warnings: AvailabilityWarning[];
  className?: string;
}

const WARNING_COPY: Record<AvailabilityWarning, { title: string; body: string }> = {
  clinic_closed: {
    title: 'Clinic marked closed',
    body: 'This day is marked closed in clinic hours. Slots are still available — confirm staffing before booking.',
  },
  provider_unavailable: {
    title: 'Provider marked unavailable',
    body: 'This provider has an unavailable override for this date. Slots are still available — confirm coverage before booking.',
  },
};

/**
 * Soft warning when availability includes slots on a closed / unavailable day.
 */
export function ScheduleAvailabilityWarning({
  warnings,
  className,
}: ScheduleAvailabilityWarningProps): JSX.Element | null {
  if (warnings.length === 0) return null;

  const unique = [...new Set(warnings)];

  return (
    <div className={cn('space-y-2', className)}>
      {unique.map((w) => {
        const copy = WARNING_COPY[w];
        return (
          <div
            key={w}
            className="rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-3"
            role="status"
          >
            <div className="flex items-start gap-2.5">
              <div className="mt-0.5 flex size-8 flex-none items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                <AlertTriangle className="size-4" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-amber-900">{copy.title}</div>
                <p className="mt-0.5 text-[11px] leading-relaxed text-amber-800/90">{copy.body}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
